import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export interface NotificationProps {
  type: 'success' | 'error' | 'warning';
  title: string;
  message?: string | undefined;
  isVisible: boolean;
  onClose: () => void;
  duration?: number; // Duration in milliseconds, default 5000
}

export const Notification: React.FC<NotificationProps> = ({
  type,
  title,
  message,
  isVisible,
  onClose,
  duration = 5000
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          border: 'border-l-4',
          borderColor: 'border-green-500 dark:border-green-400',
          icon: 'text-green-600 dark:text-green-400',
          shadow: 'shadow-xl dark:shadow-2xl',
          closeHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
          closeFocus: 'focus:ring-gray-500 dark:focus:ring-gray-400'
        };
      case 'error':
        return {
          border: 'border-l-4',
          borderColor: 'border-red-500 dark:border-red-400',
          icon: 'text-red-600 dark:text-red-400',
          shadow: 'shadow-xl dark:shadow-2xl',
          closeHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
          closeFocus: 'focus:ring-gray-500 dark:focus:ring-gray-400'
        };
      case 'warning':
        return {
          border: 'border-l-4',
          borderColor: 'border-yellow-500 dark:border-yellow-400',
          icon: 'text-yellow-600 dark:text-yellow-400',
          shadow: 'shadow-xl dark:shadow-2xl',
          closeHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
          closeFocus: 'focus:ring-gray-500 dark:focus:ring-gray-400'
        };
      default:
        return {
          border: 'border-l-4',
          borderColor: 'border-green-500 dark:border-green-400',
          icon: 'text-green-600 dark:text-green-400',
          shadow: 'shadow-xl dark:shadow-2xl',
          closeHover: 'hover:bg-gray-100 dark:hover:bg-gray-700',
          closeFocus: 'focus:ring-gray-500 dark:focus:ring-gray-400'
        };
    }
  };

  const styles = getStyles();

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className={`w-6 h-6 ${styles.icon}`} />;
      case 'error':
        return <XCircle className={`w-6 h-6 ${styles.icon}`} />;
      case 'warning':
        return <AlertCircle className={`w-6 h-6 ${styles.icon}`} />;
      default:
        return <CheckCircle className={`w-6 h-6 ${styles.icon}`} />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-2 duration-300">
      <div 
        className={`max-w-md w-full ${styles.border} ${styles.borderColor} rounded-lg p-4 ${styles.shadow} border-t border-r border-b transition-all duration-200`}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderTopColor: 'var(--color-border)',
          borderRightColor: 'var(--color-border)',
          borderBottomColor: 'var(--color-border)',
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p 
              className="text-base font-semibold leading-tight"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {title}
            </p>
            {message && (
              <p 
                className="mt-1.5 text-sm leading-relaxed"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {message}
              </p>
            )}
          </div>
          <div className="flex-shrink-0">
            <button
              onClick={onClose}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${styles.closeHover} focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.closeFocus} transition-colors duration-150`}
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Close notification"
            >
              <span className="sr-only">Close</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook for managing notifications
export const useNotification = () => {
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning';
    title: string;
    message?: string | undefined;
    isVisible: boolean;
  }>({
    type: 'success',
    title: '',
    message: '',
    isVisible: false
  });

  const showNotification = (
    type: 'success' | 'error' | 'warning',
    title: string,
    message?: string | undefined
  ) => {
    setNotification({
      type,
      title,
      message,
      isVisible: true
    });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  return {
    notification,
    showNotification,
    hideNotification
  };
};
